import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useFetchData } from 'services/api/useFetchData';
import { renderDocumentNameAndNumber, renderStageBadge, renderStatusBadge } from 'components/lists/functions/renderListColumn';
import ProposalModal from '../proposals/ProposalModal';
import JobModal from '../jobs/JobModal';
import { Ticket, TicketLinkedItem, TicketLinkedItems } from './TicketTypes';
import '../../style/scss/card.scss';

interface TicketLinkedItemsCardProps {
    ticket: Ticket; // The loaded ticket, a refreshed ticket refetches the linked items
}

// Formats a decimal amount of hours as h:mm
export const formatTrackedHours = (hours: number | string | null | undefined) => {
    const totalMinutes = Math.round(Number(hours || 0) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${m.toString().padStart(2, '0')}`;
};

// Renders the name of a linked item, the document number or the deal title
export const renderTicketLinkedItemName = (item: TicketLinkedItem) => {
    if (item.type === 'deal') return <div>{item.title}</div>;
    return renderDocumentNameAndNumber(item.number || '', item.type, item.status);
};

// Renders the status badge of a linked item
export const renderTicketLinkedItemBadge = (item: TicketLinkedItem) => {
    if (item.type === 'proposal') return renderStatusBadge(item.status || '');
    if (item.type === 'job') return item.status_name ? renderStatusBadge(item.status_name, item.status_condition) : null;
    return renderStageBadge(item.stage_name || '', item.resolution || 'open');
};

// Returns a handler which opens a linked item in its modal or on its page
export const useOpenTicketLinkedItem = () => {
    const history = useHistory();
    const { initializeModal, closeAllModals } = useModal();

    return (item: TicketLinkedItem) => {
        // Open a deal on its own page
        if (item.type === 'deal') {
            closeAllModals();
            history.push(`/deals/${item.id}`);
            return;
        }

        // Open a proposal or job in its detail modal
        const ModalComponent = item.type === 'proposal' ? ProposalModal : JobModal;
        initializeModal(<ModalComponent itemId={item.id} />, { itemId: item.id, modalSize: 'large' });
    };
};

// The card in the right column of a ticket with the proposals, jobs and deals created from it
const TicketLinkedItemsCard: React.FC<TicketLinkedItemsCardProps> = ({ ticket }) => {
    const { t } = useTranslation();
    const openLinkedItem = useOpenTicketLinkedItem();
    const { response, loading, refetch } = useFetchData({ apiUrl: `get_ticket_linked_items/${ticket.id}` });
    const [linkedItems, setLinkedItems] = useState<TicketLinkedItems | null>(null);

    // Store the fetched linked items
    useEffect(() => {
        if (response) setLinkedItems(response);
    }, [response]);

    // Refetch the linked items whenever the ticket is refreshed
    const isFirstTicketRef = useRef(true);
    useEffect(() => {
        if (isFirstTicketRef.current) {
            isFirstTicketRef.current = false;
            return;
        }
        refetch();
        // eslint-disable-next-line
    }, [ticket]);

    // Merge the linked items into one list in the order proposals, deals, jobs
    const items: TicketLinkedItem[] = linkedItems
        ? [...linkedItems.related_proposals, ...linkedItems.related_deals, ...linkedItems.related_jobs]
        : [];
    const trackedHours = Number(linkedItems?.tracked_hours || 0);

    // The card is only shown when the ticket has active linked items or registered time
    if (items.length === 0 && trackedHours === 0) return null;

    return (
        <div className='card linked-documents-card ticket-linked-items-card'>
            <div className='header'>
                <h6>{t('ticket.general.linked_items_card_title')}</h6>
            </div>
            {loading === 'show-loader' ? (
                <div className='loader' />
            ) : (
                <div className='linked-documents-list'>
                    {items.map(item => (
                        <div key={`${item.type}-${item.id}`} className='linked-document' onClick={() => openLinkedItem(item)}>
                            <div className='document-name'>{renderTicketLinkedItemName(item)}</div>
                            <span>{renderTicketLinkedItemBadge(item)}</span>
                        </div>
                    ))}
                    {trackedHours > 0 && (
                        <div className='ticket-tracked-hours'>
                            <span>{t('ticket.general.tracked_hours_label')}</span>
                            <span>{formatTrackedHours(trackedHours)} {t('date_time.general.hour.singular')}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TicketLinkedItemsCard;
